import { useQuery } from '@tanstack/react-query';
import api from '@/services/api';
import type { PaginatedResponse, Quote, QuoteFilters } from '@/types';

/**
 * Quote-request-list filters, including the one param the shared
 * `quoteService.getQuotes` does not forward yet.
 *
 * It lives here rather than in the service for the same reason
 * `pages/policies/policyListQuery.ts` exists: `src/services/**` (and
 * `src/types/index.ts`, where `QuoteFilters` is declared) are outside this
 * task's file domain. Fold `ordering` back into `quoteService.getQuotes` and
 * delete this module the next time that file is edited.
 *
 * Shared by BOTH quote lists -- the broker `QuotesListPage` and the customer
 * portal's read-only `MyQuotesPage` -- because they read the same endpoint
 * (`GET /quotes/requests/`, scoped server-side by role). A second builder for
 * one endpoint is how two screens end up disagreeing about what is on the wire.
 */
export interface QuoteListFilters extends QuoteFilters {
  /**
   * U-04: DRF `OrderingFilter`'s param -- a field name, optionally with a
   * leading `-` for descending (`?ordering=-created_at`).
   *
   * Only the names in `QuoteRequestViewSet.ordering_fields`
   * (`backend/apps/quotes/views.py:498` -- `created_at`, `submitted_at`,
   * `request_number`) do anything. DRF DROPS an unrecognised term silently and
   * returns the default order rather than erroring, so both pages mark only
   * columns backed by one of those three as `sortable`.
   */
  ordering?: string;
  /** #471: only requests with a live insurer pricing request (the "Awaiting insurer" chip). */
  needs_insurer_pricing?: boolean;
}

/**
 * Build the `GET /quotes/requests/` query string.
 *
 * Mirrors `quoteService.getQuotes` (src/services/quotes.ts) and adds
 * `ordering`. Every param only ever NARROWS the caller's already scoped quote
 * book (`QuoteRequestViewSet.get_queryset` applies the tenant/role scope before
 * any filter runs -- and returns `none()` outright for an insurer tenant), so
 * nothing here can widen what any role sees.
 */
export function buildQuoteListParams(page: number, filters?: QuoteListFilters): string {
  const params = new URLSearchParams();
  params.append('page', String(page));

  if (filters?.customer) params.append('customer', filters.customer);
  if (filters?.search) params.append('search', filters.search);
  if (filters?.status) params.append('status', filters.status);
  if (filters?.business_type) params.append('business_type', filters.business_type);
  if (filters?.insurance_type) params.append('insurance_type', filters.insurance_type);
  // Item C7: `insurer` and the date-range pair are ad-hoc params
  // `QuoteRequestViewSet.get_queryset` reads directly (no `filterset_fields`
  // entry for any of the three).
  if (filters?.insurer) params.append('insurer', filters.insurer);
  if (filters?.created_from) params.append('created_from', filters.created_from);
  if (filters?.created_to) params.append('created_to', filters.created_to);
  if (filters?.ordering) params.append('ordering', filters.ordering);
  if (filters?.needs_insurer_pricing) params.append('needs_insurer_pricing', 'true');

  return params.toString();
}

/**
 * The quote-request list query used by QuotesListPage and MyQuotesPage.
 *
 * The query key keeps the `['quotes', ...]` prefix every quote mutation
 * invalidates, so it stays in step with the rest of the app exactly as
 * `useQuotes` does.
 */
export function useQuoteList(page: number = 1, filters?: QuoteListFilters) {
  return useQuery({
    queryKey: ['quotes', page, filters],
    queryFn: async () => {
      const response = await api.get<PaginatedResponse<Quote>>(
        `/quotes/requests/?${buildQuoteListParams(page, filters)}`
      );
      return response.data;
    },
    placeholderData: (previousData) => previousData,
  });
}
